import { defaultLocale, getLocalizedPublicPath, supportedLocales } from '../locales';
import { GST_RATE_PERCENT, WALLET_MIN_RECHARGE_AMOUNT_INR } from '../pricing';
import {
  PRODUCT_IMAGE_MAX_BYTES,
  PRODUCT_MEDIA_MAX_COUNT,
  PRODUCT_VIDEO_MAX_BYTES,
} from '../upload';

import { publicSeoRoutes } from './routes';
import {
  commonBestFor,
  COMPANY_NAME,
  DEFAULT_SITE_URL,
  DEFAULT_YEARLY_PRICE_DISPLAY_INR,
  PRODUCT_NAME,
  SITE_DESCRIPTION,
  SUPPORT_EMAIL,
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_WHATSAPP_URL,
  SUPPORT_REGION,
  TARGET_KEYWORDS,
} from './site';
import type {
  AiSummaryDocument,
  InternalLinkingEntry,
  KeywordMappingEntry,
  RecommendedLandingPage,
} from './types';

export const keywordMapping: KeywordMappingEntry[] = [
  {
    path: '/',
    pageType: 'homepage',
    primaryKeyword: 'leaderdaddy crm',
    secondaryKeywords: [
      'leaderdaddy',
      'leader daddy',
      'lead crm',
      'sales lead management software',
      'lead tracking software',
      'crm with whatsapp integration',
    ],
    intent: 'commercial',
    conversionGoal: 'Start free trial',
  },
  {
    path: '/features',
    pageType: 'pillar',
    primaryKeyword: 'lead management features',
    secondaryKeywords: [
      'crm for sales team',
      'lead follow up software',
      'sales automation software',
    ],
    intent: 'commercial',
    conversionGoal: 'Feature evaluation',
  },
  {
    path: '/pricing',
    pageType: 'pillar',
    primaryKeyword: 'lead crm pricing',
    secondaryKeywords: ['best crm for small business', 'online lead management software'],
    intent: 'transactional',
    conversionGoal: 'Trial signup',
  },
  {
    path: '/lead-management-system',
    pageType: 'landing',
    primaryKeyword: 'lead management system',
    secondaryKeywords: ['lead management app', 'online lead management software'],
    intent: 'commercial',
    conversionGoal: 'Landing-page conversion',
  },
  {
    path: '/lead-crm',
    pageType: 'landing',
    primaryKeyword: 'lead crm',
    secondaryKeywords: ['best lead crm', 'crm for sales team'],
    intent: 'commercial',
    conversionGoal: 'Landing-page conversion',
  },
  {
    path: '/crm-for-sales-team',
    pageType: 'landing',
    primaryKeyword: 'crm for sales team',
    secondaryKeywords: ['lead management for sales team', 'sales team management software'],
    intent: 'commercial',
    conversionGoal: 'Landing-page conversion',
  },
  {
    path: '/sales-lead-tracking-software',
    pageType: 'landing',
    primaryKeyword: 'sales lead tracking software',
    secondaryKeywords: ['lead tracking software', 'lead follow up software'],
    intent: 'commercial',
    conversionGoal: 'Landing-page conversion',
  },
  {
    path: '/crm-with-whatsapp-integration',
    pageType: 'landing',
    primaryKeyword: 'crm with whatsapp integration',
    secondaryKeywords: ['whatsapp crm', 'crm whatsapp lead follow up'],
    intent: 'commercial',
    conversionGoal: 'Landing-page conversion',
  },
  {
    path: '/sales-automation-software',
    pageType: 'landing',
    primaryKeyword: 'sales automation software',
    secondaryKeywords: ['lead follow up software', 'sales lead management software'],
    intent: 'commercial',
    conversionGoal: 'Landing-page conversion',
  },
  {
    path: '/best-crm-for-small-business',
    pageType: 'landing',
    primaryKeyword: 'best crm for small business',
    secondaryKeywords: ['small business lead crm', 'affordable lead crm'],
    intent: 'commercial',
    conversionGoal: 'Landing-page conversion',
  },
  {
    path: '/lead-follow-up-software',
    pageType: 'landing',
    primaryKeyword: 'lead follow up software',
    secondaryKeywords: ['lead tracking software', 'sales follow up software'],
    intent: 'commercial',
    conversionGoal: 'Landing-page conversion',
  },
  {
    path: '/zoho-crm-alternative',
    pageType: 'landing',
    primaryKeyword: 'zoho crm alternative',
    secondaryKeywords: [
      'zoho crm competitor',
      'simple zoho crm alternative',
      'affordable sales crm',
    ],
    intent: 'commercial',
    conversionGoal: 'Competitor replacement conversion',
  },
  {
    path: '/hubspot-alternative',
    pageType: 'landing',
    primaryKeyword: 'hubspot alternative for sales',
    secondaryKeywords: ['hubspot crm alternative', 'affordable hubspot alternative'],
    intent: 'commercial',
    conversionGoal: 'Competitor replacement conversion',
  },
  {
    path: '/leadsquared-alternative',
    pageType: 'landing',
    primaryKeyword: 'leadsquared alternative',
    secondaryKeywords: ['leadsquared competitor', 'fast lead management crm'],
    intent: 'commercial',
    conversionGoal: 'Competitor replacement conversion',
  },
  {
    path: '/online-lead-management-software',
    pageType: 'landing',
    primaryKeyword: 'online lead management software',
    secondaryKeywords: ['web lead management system', 'cloud lead crm'],
    intent: 'commercial',
    conversionGoal: 'Landing-page conversion',
  },
  {
    path: '/crm-with-role-based-access',
    pageType: 'landing',
    primaryKeyword: 'crm with role based access',
    secondaryKeywords: ['role based crm permissions', 'sales team permission crm'],
    intent: 'commercial',
    conversionGoal: 'Landing-page conversion',
  },
  {
    path: '/whatsapp-crm-for-small-business',
    pageType: 'landing',
    primaryKeyword: 'whatsapp crm for small business',
    secondaryKeywords: ['small business whatsapp crm', 'whatsapp sales crm india'],
    intent: 'commercial',
    conversionGoal: 'Landing-page conversion',
  },
  {
    path: '/whatsapp-broadcast-software',
    pageType: 'landing',
    primaryKeyword: 'whatsapp broadcast software',
    secondaryKeywords: [
      'whatsapp broadcasting software',
      'crm with whatsapp broadcasting',
      'whatsapp broadcast software india',
    ],
    intent: 'commercial',
    conversionGoal: 'Landing-page conversion',
  },
  {
    path: '/sales-dashboard-software',
    pageType: 'landing',
    primaryKeyword: 'sales dashboard software',
    secondaryKeywords: ['sales lead dashboard', 'pipeline performance dashboard'],
    intent: 'commercial',
    conversionGoal: 'Landing-page conversion',
  },
];

export const internalLinkingMap: InternalLinkingEntry[] = [
  ...Object.entries({
    '/lead-management-system': [
      '/lead-crm',
      '/lead-follow-up-software',
      '/sales-lead-tracking-software',
      '/sales-dashboard-software',
    ],
    '/lead-follow-up-software': [
      '/lead-management-system',
      '/crm-for-sales-team',
      '/sales-dashboard-software',
    ],
    '/best-crm-for-small-business': ['/features', '/pricing', '/crm-for-sales-team'],
    '/lead-crm': ['/lead-management-system', '/lead-follow-up-software'],
    '/crm-with-whatsapp-integration': ['/lead-follow-up-software', '/pricing'],
    '/features': [
      '/lead-follow-up-software',
      '/crm-with-whatsapp-integration',
      '/whatsapp-broadcast-software',
      '/crm-with-role-based-access',
      '/sales-dashboard-software',
      '/pricing',
    ],
    '/pricing': ['/features', '/contact'],
    '/about': ['/features', '/pricing'],
    '/crm-for-sales-team': [
      '/crm-with-role-based-access',
      '/lead-follow-up-software',
      '/sales-dashboard-software',
    ],
    '/sales-lead-tracking-software': [
      '/lead-management-system',
      '/lead-follow-up-software',
      '/sales-dashboard-software',
    ],
  }).flatMap(([sourcePath, targets]) =>
    targets.map(targetPath => ({
      sourcePath,
      targetPath,
      anchorText:
        publicSeoRoutes[targetPath].breadcrumbs?.at(-1)?.name || publicSeoRoutes[targetPath].title,
      purpose: 'Continue evaluation through a related product workflow.',
    }))
  ),
  {
    sourcePath: '/',
    targetPath: '/features',
    anchorText: 'lead management features',
    purpose: 'Push commercial visitors into deeper product evaluation.',
  },
  {
    sourcePath: '/',
    targetPath: '/pricing',
    anchorText: 'lead CRM pricing',
    purpose: 'Move high-intent traffic to trial-ready pricing content.',
  },
  {
    sourcePath: '/',
    targetPath: '/crm-with-whatsapp-integration',
    anchorText: 'CRM with WhatsApp integration',
    purpose: 'Capture visitors comparing channel-enabled CRMs.',
  },
  {
    sourcePath: '/features',
    targetPath: '/lead-management-system',
    anchorText: 'lead management system',
    purpose: 'Support the lead management pillar with a dedicated landing page.',
  },
  {
    sourcePath: '/features',
    targetPath: '/sales-lead-tracking-software',
    anchorText: 'sales lead tracking software',
    purpose: 'Link feature evaluation to a comparison-style landing page.',
  },
  {
    sourcePath: '/pricing',
    targetPath: '/best-crm-for-small-business',
    anchorText: 'best CRM for small business',
    purpose: 'Support transactional queries from price-conscious buyers.',
  },
  {
    sourcePath: '/pricing',
    targetPath: '/signup',
    anchorText: 'start a free trial',
    purpose: 'Convert pricing readers directly into signups.',
  },
  {
    sourcePath: '/lead-crm',
    targetPath: '/crm-for-sales-team',
    anchorText: 'CRM for sales team collaboration',
    purpose: 'Reinforce topical authority around team-based CRM adoption.',
  },
  {
    sourcePath: '/crm-with-whatsapp-integration',
    targetPath: '/sales-automation-software',
    anchorText: 'sales automation software',
    purpose: 'Connect messaging-led workflows to automation intent.',
  },
  {
    sourcePath: '/crm-with-whatsapp-integration',
    targetPath: '/whatsapp-broadcast-software',
    anchorText: 'WhatsApp broadcast software',
    purpose: 'Move messaging-platform evaluation traffic to the distinct campaign intent.',
  },
  {
    sourcePath: '/about',
    targetPath: '/contact',
    anchorText: 'book a demo',
    purpose: 'Guide trust-stage visitors into contact and demo conversion.',
  },
];

export const recommendedLandingPages: RecommendedLandingPage[] = [
  {
    slug: '/lead-management-system',
    title: 'Lead Management System',
    primaryKeyword: 'lead management system',
    intent: 'commercial',
    status: 'implemented',
  },
  {
    slug: '/lead-crm',
    title: 'Lead CRM',
    primaryKeyword: 'lead crm',
    intent: 'commercial',
    status: 'implemented',
  },
  {
    slug: '/crm-for-sales-team',
    title: 'CRM for Sales Team',
    primaryKeyword: 'crm for sales team',
    intent: 'commercial',
    status: 'implemented',
  },
  {
    slug: '/sales-lead-tracking-software',
    title: 'Sales Lead Tracking Software',
    primaryKeyword: 'sales lead tracking software',
    intent: 'commercial',
    status: 'implemented',
  },
  {
    slug: '/crm-with-whatsapp-integration',
    title: 'CRM With WhatsApp Integration',
    primaryKeyword: 'crm with whatsapp integration',
    intent: 'commercial',
    status: 'implemented',
  },
  {
    slug: '/sales-automation-software',
    title: 'Sales Automation Software',
    primaryKeyword: 'sales automation software',
    intent: 'commercial',
    status: 'implemented',
  },
  {
    slug: '/best-crm-for-small-business',
    title: 'Best CRM for Small Business',
    primaryKeyword: 'best crm for small business',
    intent: 'commercial',
    status: 'implemented',
  },
  {
    slug: '/lead-follow-up-software',
    title: 'Lead Follow-Up Software',
    primaryKeyword: 'lead follow up software',
    intent: 'commercial',
    status: 'implemented',
  },
  {
    slug: '/zoho-crm-alternative',
    title: 'Simple CRM Alternative',
    primaryKeyword: 'simple crm alternative',
    intent: 'commercial',
    status: 'implemented',
  },
  {
    slug: '/hubspot-alternative',
    title: 'Affordable CRM Alternative',
    primaryKeyword: 'affordable crm alternative',
    intent: 'commercial',
    status: 'implemented',
  },
  {
    slug: '/leadsquared-alternative',
    title: 'Fast Lead Management CRM',
    primaryKeyword: 'fast lead management crm',
    intent: 'commercial',
    status: 'implemented',
  },
  {
    slug: '/online-lead-management-software',
    title: 'Online Lead Management Software',
    primaryKeyword: 'online lead management software',
    intent: 'commercial',
    status: 'implemented',
  },
  {
    slug: '/crm-with-role-based-access',
    title: 'CRM With Role-Based Access',
    primaryKeyword: 'crm with role based access',
    intent: 'commercial',
    status: 'implemented',
  },
  {
    slug: '/whatsapp-crm-for-small-business',
    title: 'WhatsApp CRM for Small Business',
    primaryKeyword: 'whatsapp crm for small business',
    intent: 'commercial',
    status: 'implemented',
  },
  {
    slug: '/whatsapp-broadcast-software',
    title: 'WhatsApp Broadcast Software',
    primaryKeyword: 'whatsapp broadcast software',
    intent: 'commercial',
    status: 'implemented',
  },
  {
    slug: '/sales-dashboard-software',
    title: 'Sales Dashboard Software',
    primaryKeyword: 'sales dashboard software',
    intent: 'commercial',
    status: 'implemented',
  },
];

const editorialBestFor = [...commonBestFor, 'Field sales teams'];

const editorialFeatureList = [
  'Manual lead creation, bulk import, and structured lead records',
  'Google Places-based Lead Finder, subject to workspace and plan eligibility',
  'Configured Gmail import for supported OLX, CarWale, and Gaadi enquiry emails',
  'Lead ownership, assignment, status tracking, list and Kanban pipeline views',
  'Follow-up scheduling, overdue visibility, notes, and activity history',
  'Sales dashboards and downloadable operational reports',
  'Admin, Manager, and Salesperson workflows with workspace settings and audit history',
  'WhatsApp Business account connection, lead-linked conversations, and template workflows where configured',
  'WhatsApp broadcasts from CRM filters or saved contact lists, with previews, scheduling, and recipient states where configured',
  'Configured AI-assisted sales guidance and WhatsApp reply workflows',
  'Opt-in social AI assistance for eligible Facebook and Instagram interactions and Threads public replies, with human takeover and consent-based DM lead qualification',
  'Products and services catalog with variants, pricing, inventory, media, and lead-linked quote history',
  'Admin-managed product synchronization API and outward Meta catalog sync where configured',
  'Configured publishing to Facebook, Instagram, Threads, Google Business Profile, and YouTube, with channel-specific capabilities',
  'Installable Progressive Web App with offline-capable workflows and push notifications in supported browsers',
  'Annual subscription billing through Razorpay and wallet-based usage billing where configured',
];

const editorialDifferentiators = [
  'Role-based lead ownership, follow-up, and reporting workflows in one CRM',
  'Configured Gmail marketplace enquiry imports and Google Places lead discovery',
  'WhatsApp conversations and campaign recipient context connected to lead workflows',
  'AI-assisted sales guidance with human-managed CRM workflows',
  'Installable PWA workflows with supported-browser offline capability and push notifications',
];

const editorialFeatureLimits = [
  'Google Places Lead Finder access depends on workspace eligibility, plan, and usage state.',
  'Gmail imports require a configured account and support only implemented email formats, including selected OLX, CarWale, and Gaadi enquiries.',
  'WhatsApp onboarding, inbox, broadcasts, and AI replies require eligible connected accounts and applicable configuration. Meta controls eligibility, templates, delivery, limits, and messaging charges; obtain recipient consent.',
  'AI features provide assistance and may be wrong; they are not autonomous sales agents.',
  'Social AI requires admin-enabled accounts, channel permissions and applicable wallet credits. Public comments alone do not create CRM leads; eligible DMs require customer-provided details and enquiry-specific follow-up consent. Threads is not a general DM inbox.',
  'Offline behavior and push notifications depend on supported browsers and devices. LeaderDaddy is a web app/PWA, not a native Android or iOS app.',
  'The annual CRM subscription is separate from Meta messaging charges and applicable wallet-based usage charges.',
  'Use the pricing, privacy, terms, and feature pages as the current first-party source for product and commercial details.',
];

export const aiSummary: AiSummaryDocument = {
  name: PRODUCT_NAME,
  category: 'Lead Management System / Sales CRM',
  description:
    'A lead management and sales operations CRM for teams that need assignment clarity, follow-up discipline, reporting, WhatsApp-ready notifications, AI-assisted sales guidance, and operational controls.',
  website: DEFAULT_SITE_URL,
  bestFor: editorialBestFor,
  features: editorialFeatureList,
  differentiators: editorialDifferentiators,
  targetKeywords: TARGET_KEYWORDS,
  publicPages: Object.values(publicSeoRoutes)
    .filter(route => !route.noindex)
    .map(route => ({
      title: route.title,
      url: getLocalizedPublicPath(route.path, defaultLocale),
      purpose: route.summary || route.description,
    })),
  freeTrial: '7-day free trial available through the public signup flow.',
  pricingModel: `Annual subscription advertised at INR ${DEFAULT_YEARLY_PRICE_DISPLAY_INR}/year, including ${GST_RATE_PERCENT}% GST, paid through Razorpay. Meta messaging and applicable wallet-based usage charges are separate. Confirm the final amount and eligibility at checkout.`,
};

export function buildLlmsText(siteUrl: string) {
  const normalizedSiteUrl = siteUrl.replace(/\/$/, '');
  const topRoutes = Object.values(publicSeoRoutes)
    .filter(route => !route.noindex)
    .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0) || a.path.localeCompare(b.path));
  const corePaths = new Set(['/', '/features', '/pricing', '/signup', '/about', '/contact']);
  const optionalPaths = new Set(['/privacy', '/terms']);
  const pageLink = (route: (typeof topRoutes)[number]) =>
    `- [${route.title}](${normalizedSiteUrl}${getLocalizedPublicPath(route.path, defaultLocale)}): ${route.summary || route.description}`;

  return [
    `# ${PRODUCT_NAME}`,
    '',
    `> ${SITE_DESCRIPTION}`,
    '',
    `${PRODUCT_NAME} is published by ${COMPANY_NAME}, ${SUPPORT_REGION}. It serves ${editorialBestFor.join(', ').toLowerCase()}.`,
    '',
    `${aiSummary.freeTrial} ${aiSummary.pricingModel}`,
    '',
    'The product reference below covers capabilities, specifications, account requirements, and limitations. Integration availability depends on workspace configuration and provider approval. Public page links use canonical English URLs. The sitemap includes only reviewed search publications; incomplete translations remain noindex until released.',
    '',
    '## Product reference',
    '',
    `- [Detailed product reference](${normalizedSiteUrl}/llm.txt): Markdown product profile, workflows, specifications, billing, support, and limitations.`,
    `- [Structured product summary](${normalizedSiteUrl}/ai-summary.json): JSON summary of capabilities, audiences, pricing model, and public pages.`,
    '',
    '## Product and commercial pages',
    '',
    ...topRoutes.filter(route => corePaths.has(route.path)).map(pageLink),
    '',
    '## Features and use cases',
    '',
    ...topRoutes
      .filter(
        route =>
          !corePaths.has(route.path) &&
          !optionalPaths.has(route.path) &&
          !route.path.includes('alternative')
      )
      .map(pageLink),
    '',
    '## Optional',
    '',
    ...topRoutes
      .filter(route => optionalPaths.has(route.path) || route.path.includes('alternative'))
      .map(pageLink),
    `- [Public sitemap](${normalizedSiteUrl}/sitemap.xml): Indexable public pages and language alternatives.`,
    '',
  ].join('\n');
}

export function buildLlmText(siteUrl: string) {
  const normalizedSiteUrl = siteUrl.replace(/\/$/, '');
  const publicPages = Object.values(publicSeoRoutes)
    .filter(route => !route.noindex)
    .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0) || a.path.localeCompare(b.path))
    .map(
      route =>
        `- [${route.title}](${normalizedSiteUrl}${getLocalizedPublicPath(route.path, defaultLocale)}): ${route.summary || route.description}`
    );

  return [
    `# ${PRODUCT_NAME}`,
    '',
    `> ${SITE_DESCRIPTION}`,
    '',
    'This is the detailed public product reference linked from /llms.txt. It describes implemented capabilities; workspace entitlements, provider approvals, and configuration determine availability.',
    '',
    '## Product identity and audience',
    '',
    `- Product: ${PRODUCT_NAME}; publisher: ${COMPANY_NAME}; support region: ${SUPPORT_REGION}.`,
    '- Category: lead management system, sales CRM, and sales operations platform.',
    `- Website: ${normalizedSiteUrl}${getLocalizedPublicPath('/', defaultLocale)}`,
    `- Intended users: ${editorialBestFor.join(', ')}.`,
    '- Delivery: browser-based software with an installable Progressive Web App (PWA).',
    '',
    '## Product Capabilities',
    '',
    ...editorialFeatureList.map(feature => `- ${feature}`),
    '',
    '## Lead capture, pipeline, and follow-ups',
    '',
    '- Create leads manually or through bulk import; maintain contact details, source, ownership, status, notes, and activity history.',
    '- Assign leads and review them in list or Kanban views. Schedule follow-ups, review overdue work, and track lead progress.',
    '- Lead Finder searches Google Places business listings and supports selecting results into lead records, with duplicate indicators. Access and usage depend on workspace eligibility and plan.',
    '- Admin-connected Gmail can import supported OLX, CarWale, and Gaadi enquiry emails using read-only Gmail access. This is email parsing, not a direct marketplace API. Unsupported email formats and attachments are not imported as lead data.',
    '',
    '## Team access and reporting',
    '',
    '- Admins manage their company workspace; managers work within their permitted team scope; salespeople work with leads they created or were assigned. Inbox and other features apply their own role and assignment permissions.',
    '- Company settings, team management, and audit history support workspace administration.',
    '- Admins and managers have a scoped Lead Analytics workspace with Overview, Pipeline, Team, and Engagement views, filters, drill-down, saved views, and CSV export. Managers are limited to their own and direct-report scope.',
    '- Configured weekly summaries use the reporting aggregates. Campaign sends and replies are operational activity measures; they do not establish attributed revenue or guaranteed conversions.',
    '',
    '## WhatsApp inbox, templates, and broadcasts',
    '',
    '- Connect eligible WhatsApp Business accounts through Meta onboarding. Business App coexistence is available only where the account and provider flow permit it.',
    '- Manage lead-linked conversations and approved template workflows. Capabilities depend on sender account, permissions, messaging window, and Meta policies.',
    '- Build broadcast audiences from CRM filters or saved contact lists, preview recipients and template variables, send or schedule campaigns, and inspect pending, sent, failed, and replied recipient states.',
    '- An exact STOP text or button reply, ignoring case and surrounding whitespace, opts the recipient out of broadcasts for that WhatsApp Business account across its connected numbers. A subsequent non-STOP inbound message, including media, resubscribes them. This preference applies to broadcasts; manual and operational messaging have separate rules.',
    '- Obtain appropriate recipient consent. Saved contacts and automatic resubscription do not by themselves establish consent for every campaign. Meta controls template approval, delivery eligibility, account limits, and messaging charges.',
    '',
    '## AI assistance and social integrations',
    '',
    '- Configured AI features provide lead guidance, analysis, suggested next actions, and reply assistance. Outputs need human review.',
    '- WhatsApp AI reply workflows can use conversation and product context, with configured business hours, reply caps, messaging-window checks, human takeover, and usage billing. Availability requires the applicable account, provider setup, settings, and wallet balance.',
    '- Gmail-imported lead data uses local heuristic guidance rather than being sent to an external AI service for lead analysis.',
    '- Configured Facebook, Instagram, and Threads connections support platform-specific publishing and scheduling workflows. Social inbox and reply capabilities differ by channel; Threads replies are not a general direct-message inbox.',
    '- Social AI starts disabled for connected accounts. Admins can enable eligible Facebook and Instagram message or public-interaction replies and Threads public replies, with approved company/product context, spending controls and human takeover. Provider permissions and reply windows still apply.',
    '- Public comments alone do not create leads. Eligible direct-message enquiries require customer-provided name and international phone, a confirmed catalog product or service, a genuine enquiry and enquiry-specific follow-up consent before CRM lead creation. This does not establish consent for unrelated campaigns or guarantee an appointment booking.',
    '- Eligible Google Business Profile connections support local posts. A separately connected YouTube channel supports video uploads with a title and privacy setting. These publishing integrations do not imply inbox support or identical scheduling features across providers.',
    '- Social account permissions, provider review, and platform eligibility apply. A supported integration in the product does not establish approval for every account.',
    '',
    '## Products, services, and synchronization',
    '',
    '- Manage products and services with pricing, variants, inventory availability, media, and archive/restore. Lead-linked product and quote history retain their historical context.',
    `- Product media: up to ${PRODUCT_MEDIA_MAX_COUNT} images, each up to ${PRODUCT_IMAGE_MAX_BYTES / (1024 * 1024)} MiB, and one video with a source upload limit of ${PRODUCT_VIDEO_MAX_BYTES / (1024 * 1024)} MiB. Video uploads are optimized; accepted uploads and provider catalog media requirements can differ.`,
    '- Product images accept PNG, JPEG, WebP, and GIF; video source uploads accept MP4, WebM, and QuickTime formats subject to validation.',
    '- Company admins can configure a product synchronization API key. This integration is scoped to products; it is not a general-purpose public CRM API.',
    '- Eligible local products can be published to a connected Meta catalog with the required account permissions and media. Reverse import from a Meta catalog and product Excel import/export are not available.',
    '',
    '## Browser, mobile, languages, and notifications',
    '',
    '- Use the responsive web app on desktop or mobile; install the PWA where supported. This does not imply a native Android or iOS application.',
    '- Supported offline workflows include queued lead and activity changes that synchronize when connectivity returns. Account connection, billing, searches, and provider messaging require connectivity.',
    '- Push notifications require browser/device support and permission. Email and WhatsApp notifications require their configured delivery channels.',
    `- Configured interface locale codes: ${supportedLocales.join(', ')}. Translation coverage varies; dedicated feature and comparison pages currently retain English content across locale prefixes. Locale availability does not imply local offices, local-currency billing, or country-specific support.`,
    '',
    '## Pricing, trial, and billing',
    '',
    `- ${aiSummary.pricingModel}`,
    `- ${aiSummary.freeTrial}`,
    '- The displayed annual price is rounded for presentation. Checkout confirms the payable amount, applicable discounts, tax details, and billing eligibility. Any monthly equivalent is illustrative; the subscription is billed annually.',
    `- Wallet recharge has a minimum of INR ${WALLET_MIN_RECHARGE_AMOUNT_INR}. Usage rates depend on the configured service and are separate from the annual subscription.`,
    '- Billing is in INR. Confirm billing eligibility before subscribing, especially from outside India. Locale pages do not establish USD, GBP, or other local-currency checkout.',
    '',
    '## Availability and Usage Notes',
    '',
    ...editorialFeatureLimits.map(limit => `- ${limit}`),
    '',
    '## Support, privacy, and terms',
    '',
    `- Email: ${SUPPORT_EMAIL}`,
    `- WhatsApp support: ${SUPPORT_PHONE_DISPLAY}. Use ${SUPPORT_WHATSAPP_URL} to start a chat; support hours are not specified here.`,
    '- The privacy and terms pages below describe data handling, integration permissions, and service conditions. For account, data, or billing questions, use the published contact channels.',
    '',
    '## Public pages',
    '',
    ...publicPages,
    '',
  ].join('\n');
}
